import { AgentTargetViewModel } from "../ViewModels";
import {
    ActionResponse,
    ActionStatusHelper,
    DomainDetailHook,
    useDetailLifeCycle,
} from "@/Shared";

import { DOMAIN_CONFIGS, DomainHookProps } from "@/Shared/Config/domain-config";
import { useAgentTarget } from "@/OneLiners";
import { useAgentTargetEnricher } from "./useAgentTrgetEnricher";
import { AgentTargetModel } from "../AgentTargetModel";


/**
 * useAgentTargetDetail hook
 *
 * Domain-specific detail hook for AgentTarget entities.
 * Acts as the orchestration layer between:
 * - domain data access and enrichment,
 * - UI feedback mechanisms (toast notifications, confirmation dialogs),
 * - and navigation concerns.
 *
 * Responsibilities:
 * - Initialize the AgentTargetViewModel when data is available.
 * - Handle delete operations with access control, confirmation modal, and toast feedback.
 * - Manage navigation (form, detail view, layout) based on user actions and permissions.
 * - Expose entity VM, delete handler, access rights, and navigation methods to the UI layer.
 * 
 * @date: March 2, 2026
 * @author: Samano CASTRE
 * 
 * @see DomainHookProps
 * @see DomainDetailHook<AgentTargetViewModel>
 *
 * @param props DomainHookProps
 * @returns  DomainDetailHook<AgentTargetViewModel>
 */
export function useAgentTargetDetail(props: DomainHookProps): DomainDetailHook<AgentTargetViewModel> {
    const { permissionHook, accessRight } = props;
    const { domainType } = DOMAIN_CONFIGS.AgentTarget;

    /**
     * Enrichment layer
     *
     * Responsible for resolving user-related data and
     * computing derived AgentTarget fields.
     */
    const {
        userMap,
        getStrictEnrichedAgentTarget,
        enricherResponse,
    } = useAgentTargetEnricher();


    /**
     * AgentTarget domain CRUD operations and state
     */
    const agentTargetHook = useAgentTarget();

    /**
     * Combined response acting as a single readiness contract
     * for initializing the detail view
     */
    const response: ActionResponse = {
        status: ActionStatusHelper.combine(
            agentTargetHook.stateResponse.status,
            enricherResponse.status,
            permissionHook.stateResponse.status
        ),
        error: agentTargetHook.stateResponse.error ??
            enricherResponse.error
    };

    /**
     * Detail life cycle hook
     *
     * Handles:
     * - Entity resolution (queryId / entityId)
     * - ViewModel construction
     * - Post-fetch or post-update handling
     * - Provides public API to the UI layer
     */
    const lifeCycleHook = useDetailLifeCycle({
        response,
        domainType,
        props,
        entityHook: agentTargetHook,
        buildVM: (currentEntity: AgentTargetModel, isDisplaying: boolean) => {
            return new AgentTargetViewModel({
                agentTarget: getStrictEnrichedAgentTarget(currentEntity),
                accessRight,
                isDisplaying
            })
        },
    });

    /**
     * Public API exposed to the UI layer
     */
    return {
        ...lifeCycleHook
    }
}